import type { ExtractedEvent } from '../shared/contracts';
import type { DateCandidate } from './date-candidates';

export const MAX_EVENTS_PER_MESSAGE = 12;
const DAY = 86_400_000;

function validDay(value: string): boolean {
  const date = new Date(`${value}T00:00:00Z`);
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function validEventTimes(event: Pick<ExtractedEvent, 'startDateTime' | 'endDateTime' | 'allDay'>): boolean {
  const valid = (value: string) => validDay(value.slice(0, 10)) && (event.allDay
    ? value.length === 10
    : /^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value)));
  if (!event.startDateTime || !valid(event.startDateTime)) return false;
  if (!event.endDateTime) return true;
  if (!valid(event.endDateTime)) return false;
  const duration = Date.parse(event.endDateTime) - Date.parse(event.startDateTime);
  return duration > 0 && duration <= 31 * DAY;
}

/** AI output cannot invent a new date or extend beyond a date range found by deterministic parsing. */
export function groundedEvent(event: ExtractedEvent, candidates: DateCandidate[]): boolean {
  if (!validEventTimes(event)) return false;
  return candidates.some((candidate) => {
    if (candidate.start.slice(0, 10) !== event.startDateTime!.slice(0, 10)) return false;
    if (!event.endDateTime) return true;
    const endDay = event.endDateTime.slice(0, 10);
    const latest = candidate.end?.slice(0, 10) ?? (event.allDay
      ? new Date(Date.parse(candidate.start.slice(0, 10)) + DAY).toISOString().slice(0, 10)
      : candidate.start.slice(0, 10));
    return endDay <= latest;
  });
}
